// Per-route SEO metadata + service detail content for both CZ and EN.
// Each route entry is keyed by a logical id. The CZ/EN paths are 1:1 mirrors
// (used by the language switcher and hreflang tags). Service detail entries
// also embed the long-form body content rendered on /sluzby/<slug>/ pages.

export const SITE_URL = "https://www.itimpact.cz";

// Logical ids -> { cs: pathCz, en: pathEn }. The order here also drives the
// services index list, the sitemap and the language switcher mapping.
export const ROUTE_MAP = {
  home: { cs: "/", en: "/en/" },
  services: { cs: "/sluzby/", en: "/en/services/" },
  "service.mentoring": {
    cs: "/sluzby/mentoring-it-manazeru/",
    en: "/en/services/it-manager-mentoring/",
  },
  "service.consulting": {
    cs: "/sluzby/konzultace-audit-it-tymu/",
    en: "/en/services/it-team-consulting-audit/",
  },
  "service.softSkills": {
    cs: "/sluzby/skoleni-workshopy/",
    en: "/en/services/soft-skills-training/",
  },
  "service.agile": {
    cs: "/sluzby/agile-scrum-skoleni/",
    en: "/en/services/agile-scrum-training/",
  },
  "service.okr": {
    cs: "/sluzby/okr-workshop/",
    en: "/en/services/okr-workshop/",
  },
  "service.change": {
    cs: "/sluzby/change-management-it/",
    en: "/en/services/change-management-workshop/",
  },
};

export const SERVICE_KEYS = [
  "service.mentoring",
  "service.consulting",
  "service.softSkills",
  "service.agile",
  "service.okr",
  "service.change",
];

const OG_IMAGE = `${SITE_URL}/og-image.jpg`;

// Per-route metadata. Each entry has shape:
// { title, description, keywords, h1 (if standalone page), bodyKey (optional) }
export const SEO_DATA = {
  cs: {
    home: {
      title: "IT Impact | Školení, mentoring a change management pro IT firmy",
      description:
        "Pomáhám IT firmám zlepšit efektivitu, leadership a zvládání změn pomocí praktických školení, mentoringu a konzultací.",
      keywords:
        "školení pro IT firmy, mentoring manažerů, agile školení, scrum školení, OKR workshop, change management v IT, konzultace vývoje software, audit IT týmu",
    },
    services: {
      title: "Služby | Školení, mentoring a konzultace pro IT firmy — IT Impact",
      description:
        "Přehled služeb IT Impact: mentoring IT manažerů, konzultace a audit IT týmů, Agile/Scrum školení, OKR workshopy a change management v IT.",
      keywords:
        "služby IT konzultace, školení IT manažerů, audit IT týmu, agile workshopy, OKR školení",
      h1: "Služby pro IT firmy a manažery",
    },
    "service.mentoring": {
      title:
        "Mentoring IT manažerů a team leadů | Praktický rozvoj leadershipu — IT Impact",
      description:
        "1:1 mentoring pro IT manažery a team leady. Prioritizace, delegace, zpětná vazba, rozhodování pod tlakem — postavené na vašich reálných situacích.",
      keywords:
        "mentoring manažerů, mentoring team leadů, leadership pro IT manažery, mentoring engineering manager, IT manager coaching",
      h1: "Mentoring pro IT manažery a team leady",
    },
    "service.consulting": {
      title:
        "Konzultace a audit IT týmu | Senior poradenství pro IT firmy — IT Impact",
      description:
        "Audit IT týmu a senior konzultace pro firmy, které potřebují zrychlit delivery, snížit incidenty nebo zlepšit organizační design.",
      keywords:
        "audit IT týmu, konzultace vývoje software, zlepšení efektivity vývojového týmu, IT poradenství, engineering audit",
      h1: "Konzultace a audit IT týmu",
    },
    "service.softSkills": {
      title:
        "Soft Skills školení pro IT manažery a týmy | Praktické workshopy — IT Impact",
      description:
        "Praktické soft-skills školení pro IT lídry — leadership, zpětná vazba, koučování, řešení konfliktů, asertivita, time management. Vše na reálných situacích.",
      keywords:
        "soft skills školení, školení pro IT manažery, leadership trénink, školení komunikace, školení zpětné vazby",
      h1: "Soft Skills školení a workshopy",
    },
    "service.agile": {
      title:
        "Agile a Scrum školení pro IT týmy | Praktické workshopy — IT Impact",
      description:
        "Agile a Scrum školení zaměřené na výsledky, ne na ceremonie. Praktické cvičení s vaším backlogem, metriky toku, retrospektivy, refinement.",
      keywords:
        "agile školení, scrum školení, agile workshop, scrum master školení, kanban školení, agile training",
      h1: "Agile a Scrum školení pro IT týmy",
    },
    "service.okr": {
      title:
        "OKR Workshop a certifikace | Propojení strategie a realizace — IT Impact",
      description:
        "Akreditovaný OKR Mentor — praktická certifikace OKR, návrh Objectives a Key Results na míru, facilitace prvního cyklu, check-iny a retrospektivy.",
      keywords:
        "OKR workshop, OKR školení, OKR certifikace, objectives and key results, OKR Mentor",
      h1: "OKR Workshop a OKR Certifikace",
    },
    "service.change": {
      title:
        "Change Management v IT | Workshop a podpora při zavádění změn — IT Impact",
      description:
        "Change management workshop pro IT firmy. Praktické modely (Kotter, ADKAR), stakeholder mapping, komunikační plán a follow-up po skončení.",
      keywords:
        "change management školení, change management v IT, řízení změn, transformace IT, change management workshop",
      h1: "Change Management v IT",
    },
  },
  en: {
    home: {
      title:
        "IT Impact | IT Leadership Mentoring, Agile Training and Change Management",
      description:
        "Practical consulting, mentoring and workshops for IT leaders who need to improve delivery, leadership and organizational change without rigid frameworks.",
      keywords:
        "IT leadership mentoring Czech Republic, agile training for IT teams, scrum training Prague, OKR workshop, change management workshop, software team consulting Czech Republic, engineering manager mentoring",
    },
    services: {
      title:
        "Services | Mentoring, Training and Consulting for IT Teams — IT Impact",
      description:
        "Overview of IT Impact services: IT manager mentoring, IT team consulting & audit, Agile/Scrum training, OKR workshops and change management for IT.",
      keywords:
        "IT consulting services, engineering manager mentoring, IT team audit, agile workshops, OKR training",
      h1: "Services for IT companies and leaders",
    },
    "service.mentoring": {
      title:
        "IT Manager Mentoring | Engineering Leadership Coaching — IT Impact",
      description:
        "1:1 mentoring for IT managers and team leads. Prioritization, delegation, feedback, decision-making under pressure — grounded in your real situations.",
      keywords:
        "IT manager mentoring, engineering manager mentoring, team lead coaching, IT leadership coaching",
      h1: "Mentoring for IT Managers and Team Leads",
    },
    "service.consulting": {
      title:
        "IT Team Consulting & Audit | Senior Advisory for Tech Companies — IT Impact",
      description:
        "Senior consulting and IT team audits for companies that need to accelerate delivery, reduce incidents or improve organizational design.",
      keywords:
        "software team consulting Czech Republic, IT team audit, engineering audit, IT consulting Prague",
      h1: "IT Team Consulting and Audit",
    },
    "service.softSkills": {
      title:
        "Soft Skills Training for IT Managers and Teams | Practical Workshops — IT Impact",
      description:
        "Hands-on soft skills training for IT leaders — leadership, feedback, coaching, conflict resolution, assertiveness, time management. Built on real cases.",
      keywords:
        "soft skills training, IT leadership training, communication training, feedback training",
      h1: "Soft Skills Training and Workshops",
    },
    "service.agile": {
      title:
        "Agile and Scrum Training for IT Teams | Practical Workshops — IT Impact",
      description:
        "Agile and Scrum training focused on outcomes, not ceremonies. Hands-on exercises with your real backlog, flow metrics, retrospectives, refinement.",
      keywords:
        "agile training for IT teams, scrum training Prague, agile workshop, kanban training",
      h1: "Agile and Scrum Training for IT Teams",
    },
    "service.okr": {
      title:
        "OKR Workshop and Certification | Aligning Strategy and Execution — IT Impact",
      description:
        "Accredited OKR Mentor — practical OKR certification, tailored design of Objectives and Key Results, facilitation of the first cycle, check-ins and retros.",
      keywords:
        "OKR workshop for technology companies, OKR training, OKR certification, OKR Mentor",
      h1: "OKR Workshop and OKR Certification",
    },
    "service.change": {
      title:
        "Change Management Workshop for IT Organizations — IT Impact",
      description:
        "Change management workshops for IT companies. Practical models (Kotter, ADKAR), stakeholder mapping, communication plan and 30-day follow-up.",
      keywords:
        "change management workshop for IT organizations, change management for IT, IT transformation, change leadership",
      h1: "Change Management for IT Organizations",
    },
  },
};

// Long-form per-service content for the detail pages. Mirrored CZ/EN.
export const SERVICE_CONTENT = {
  cs: {
    "service.mentoring": {
      eyebrow: "— Mentoring —",
      lead:
        "1:1 mentoring pro IT manažery, team leady i engineering managery, kteří chtějí zrychlit svůj rozvoj bez prázdných motivačních frází. Pracujeme na situacích, které řešíte právě teď.",
      sections: [
        {
          title: "Pro koho je mentoring určen",
          body: "Nově povýšení manažeři, team leadi rostoucí do role engineering managera, zkušení manažeři pod tlakem reorganizace, senior individuální přispěvatelé hledající přechod do leadershipu. Pracoval jsem s lidmi v korporacích (BNP Paribas, ThermoFisher, PwC, IBM, Kyndryl) i ve scale-up firmách.",
        },
        {
          title: "Co spolu během sezení uděláme",
          body: "Strukturované koučovací otázky, sparing nad konkrétními rozhodnutími, hledání nejlepší cesty ve vašem kontextu. Klíčové dovednosti: prioritizace, delegace, zpětná vazba, vedení obtížných rozhovorů, rozhodování pod tlakem, ochrana focusu vlastního i týmu.",
        },
        {
          title: "Konkrétní výstupy",
          body: "Z každého sezení odcházíte s definovaným dalším krokem. Mezi hovory je k dispozici ad-hoc kontakt (e-mail, krátké zprávy), když něco nemůže počkat na další sezení.",
        },
        {
          title: "Formát a frekvence",
          body: "60–90 minut, typicky 2× měsíčně, on-site nebo remote (Google Meet / MS Teams). Doporučený minimální cyklus jsou 3 měsíce, abyste reálně viděli posun.",
        },
      ],
      bullets: [
        "Zrychlený rozvoj reálných manažerských dovedností",
        "Pravidelná zpětná vazba a sparing v jednom",
        "Ad-hoc podpora mezi sezeními",
        "Praktické šablony a frameworky",
        "Bez dogmatu — řešení sedí vaší realitě",
      ],
    },
    "service.consulting": {
      eyebrow: "— Konzultace & Audit —",
      lead:
        "Objektivní senior pohled na váš delivery, organizační design a leadership. Pomůžu vám najít úzká místa a navrhnout konkrétní zlepšení, která reálně zvýší efektivitu vývojového týmu.",
      sections: [
        {
          title: "Kdy má audit smysl",
          body: "Pomalá nebo nepředvídatelná dodávka, časté production incidenty, vysoká fluktuace seniorů, chaos v prioritách, slabá komunikace mezi týmy nebo s byznysem. Také před rozhodnutím o reorganizaci, dělení týmů nebo větším náboru.",
        },
        {
          title: "Co audit obsahuje",
          body: "Strukturované rozhovory s manažery, team leady i vývojáři. Analýza klíčových procesů (planning, refinement, retrospektivy, code review, release management, incident management). Pohled na metriky toku, kapacitu a strukturu týmů.",
        },
        {
          title: "Výstup",
          body: "Konkrétní zpráva s prioritizovanými doporučeními (P0/P1/P2), odhadem dopadu a návrhem prvních 30/60/90 dnů. Pokud má smysl, navážeme follow-up podporou při implementaci.",
        },
        {
          title: "Délka spolupráce",
          body: "Typicky 2–4 týdny pro audit, následně 0–6 měsíců hands-on podpory dle rozsahu změn. Bez závazku dlouhého kontraktu — pracujeme po fázích, ze kterých můžete kdykoli vystoupit.",
        },
      ],
      bullets: [
        "Audit klíčových procesů a struktury",
        "Analýza metrik toku a kapacity",
        "Prioritizovaná doporučení s odhadem dopadu",
        "Hands-on podpora při implementaci",
        "Žádný dogmatický framework navíc",
      ],
    },
    "service.softSkills": {
      eyebrow: "— Soft Skills —",
      lead:
        "Praktické soft-skills školení pro IT manažery a klíčové členy týmů. Stavíme na reálných případech z vašeho prostředí, ne na obecných teoretických modelech.",
      sections: [
        {
          title: "Klíčové oblasti",
          body: "Motivace, Leadership, Dávání a přijímání zpětné vazby, Koučování, Přesvědčování, Asertivita, Řešení konfliktů, Facilitace a moderace, Stanovení cílů, Time management, Vyhoření (Burnout). Každou lze sbalit do samostatného modulu nebo do širšího programu.",
        },
        {
          title: "Formát školení",
          body: "0,5–1 den interaktivní formát s role-play scénáři, vodítky pro reálné rozhovory a konkrétními rámci, které účastníci mohou použít hned další den. On-site nebo remote (max 12 lidí pro plnou interakci).",
        },
        {
          title: "Příprava na míru",
          body: "Před školením krátký intro hovor s objednavatelem (manažer/HR) — zjistíme reálné situace, které tým zažívá. Cvičení a role-playe pak postavíme přímo na nich, ne na abstraktních příkladech.",
        },
        {
          title: "Pro koho",
          body: "Engineering managery, team leady, scrum mastery, product ownery, projektové manažery a klíčové seniory, kteří dělají faktickou nebo neformální leadership práci.",
        },
      ],
      bullets: [
        "11 modulárních oblastí",
        "Reálné situace z vašeho prostředí",
        "Role-play scénáře a šablony",
        "0,5–1 den intenzivní formát",
        "Take-home materiály pro okamžité použití",
      ],
    },
    "service.agile": {
      eyebrow: "— Agile / Scrum —",
      lead:
        "Praktické Agile a Scrum školení pro IT týmy, které chtějí výsledky, ne další ceremonie. Pracujeme s vaším reálným backlogem a metrikami, ne s teoretickými příklady.",
      sections: [
        {
          title: "Pro koho je školení určeno",
          body: "Týmy, které začínají s Agile/Scrumem. Týmy, které „dělají Scrum, ale nemají výsledky“. Scrum mastery a product owneři hledající posun nad rámec základního certifikátu. Engineering managery, kteří chtějí porozumět tomu, co od týmu reálně očekávat.",
        },
        {
          title: "Co spolu probereme",
          body: "Agile myšlení a principy (proč, ne jen jak). Praktické metriky toku — lead time, throughput, WIP, cycle time. Kanban a Scrum v reálné praxi. Refinement, planning, retrospektivy, které mají smysl. Práce s technickým dluhem a kapacitou.",
        },
        {
          title: "Praktická cvičení",
          body: "Backlog refinement nad vaším reálným backlogem. Analýza metrik z vašeho toolu (Jira, Linear, GitHub Projects). Návrh retrospektivního formátu, který tým reálně využije. Stand-up bez prázdného předčítání statusu.",
        },
        {
          title: "Délka a formát",
          body: "Workshop 1–2 dny (on-site doporučeno pro plnou interakci), volitelně navazující 30denní coaching support, kdy s týmem řešíme reálné situace v rychlém režimu.",
        },
      ],
      bullets: [
        "Agile myšlení, ne jen ceremonie",
        "Praktické metriky toku",
        "Práce s vaším backlogem a daty",
        "Retrospektivy, které tým reálně využije",
        "Volitelný 30denní follow-up coaching",
      ],
    },
    "service.okr": {
      eyebrow: "— OKR —",
      lead:
        "Jsem akreditovaný OKR Mentor a pomáhám IT firmám zavést OKR praktickým, udržitelným způsobem, který propojí strategii s realizací. Bez slepého kopírování Google modelu.",
      sections: [
        {
          title: "OKR Certifikační školení",
          body: "1–2denní certifikační školení pro vedení, manažery i OKR championy. Probereme rozdíl mezi OKR a KPI, jak psát kvalitní Objectives a měřitelné Key Results, jak nastavit cyklus, check-iny a retrospektivy. Účastníci získávají certifikát.",
        },
        {
          title: "Implementační podpora",
          body: "Pomohu s prvním cyklem na míru: alignment vedení, návrh stromu OKR napříč organizací, facilitace prvního OKR settingu, setup check-in rytmu (typicky týdenní/dvoutýdenní) a retrospektivy po skončení cyklu.",
        },
        {
          title: "Restart nebo zlepšení existující praxe",
          body: "Pokud už OKR máte, ale necítíte výsledky — udělám audit aktuálního stavu (Objectives, KR, rytmus, role) a navrhnu konkrétní zlepšení. Často stačí 2–3 změny, aby praxe začala reálně přinášet hodnotu.",
        },
        {
          title: "Pro koho je to vhodné",
          body: "IT firmy, scale-upy a oddělení velkých korporací, které chtějí zlepšit prioritizaci a alignment napříč týmy. Typicky 20+ lidí — pod touto hranicí často stačí lehčí formát řízení cílů.",
        },
      ],
      bullets: [
        "Akreditovaný OKR Mentor",
        "Certifikační školení 1–2 dny",
        "Facilitace prvního cyklu na míru",
        "Audit a restart existující praxe",
        "Bez slepého kopírování cizích modelů",
      ],
    },
    "service.change": {
      eyebrow: "— Change Management —",
      lead:
        "Praktický change management pro IT firmy, které procházejí reorganizací, technologickou změnou, akvizicí nebo rapidním růstem. Bez dogmatu, s konkrétními příklady z praxe.",
      sections: [
        {
          title: "Kdy má smysl",
          body: "Reorganizace nebo dělení týmů. Změna nástrojů (např. monolit → microservices, on-prem → cloud). Akvizice nebo integrace nového týmu. Zavádění nového delivery modelu (waterfall → agile, ad-hoc → strukturovaný). Rapidní růst nad 30+ lidí, kdy přestávají fungovat dosavadní postupy.",
        },
        {
          title: "Workshop pro vedení a klíčové lidi",
          body: "1denní workshop, 8–20 lidí. Probereme modely change managementu (Kotter, ADKAR, Bridges Transition Model) přizpůsobené IT realitě. Stakeholder mapping, komunikační plán, plán prvních 30/60/90 dnů. Praktická cvičení na vašem konkrétním change.",
        },
        {
          title: "Follow-up podpora",
          body: "Volitelná 30–90denní podpora po workshopu — pravidelné check-iny s vedením, sparing v krizových momentech, podpora s komunikací směrem k týmům. Bez fakturace za drobné dotazy.",
        },
        {
          title: "Co reálně získáte",
          body: "Konkrétní akční plán, který tým dokáže provést. Společnou řeč mezi vedením a manažery o tom, jak se změna povede. Snížení rizika rezignací a interní opozice. Rychlejší stabilizaci po change.",
        },
      ],
      bullets: [
        "Modely Kotter / ADKAR v IT realitě",
        "Workshop 1 den pro 8–20 lidí",
        "Stakeholder mapping a komunikační plán",
        "Plán 30/60/90 dnů",
        "Volitelný 30–90denní follow-up",
      ],
    },
  },
  en: {
    "service.mentoring": {
      eyebrow: "— Mentoring —",
      lead:
        "1:1 mentoring for IT managers, team leads and engineering managers who want to accelerate their development without empty motivational frameworks. We work on the situations you are facing right now.",
      sections: [
        {
          title: "Who it is for",
          body: "Newly promoted managers, team leads growing into an engineering manager role, experienced managers under reorganization pressure, senior individual contributors transitioning into leadership. I have worked with people in corporations (BNP Paribas, ThermoFisher, PwC, IBM, Kyndryl) as well as in scale-ups.",
        },
        {
          title: "What we actually do in sessions",
          body: "Structured coaching questions, sparring on concrete decisions, jointly finding the best path in your context. Key skills: prioritization, delegation, feedback, leading difficult conversations, decision-making under pressure, protecting your own and the team's focus.",
        },
        {
          title: "Concrete outputs",
          body: "Each session ends with a defined next step. Between sessions you have ad-hoc access (email, short messages) for things that cannot wait.",
        },
        {
          title: "Format and frequency",
          body: "60–90 minutes, typically twice per month, on-site or remote (Google Meet / MS Teams). Recommended minimum cycle is 3 months to actually see movement.",
        },
      ],
      bullets: [
        "Accelerated growth of real management skills",
        "Regular feedback and sparring in one place",
        "Ad-hoc support between sessions",
        "Practical templates and frameworks",
        "No dogma — solutions fit your reality",
      ],
    },
    "service.consulting": {
      eyebrow: "— Consulting & Audit —",
      lead:
        "Objective senior perspective on your delivery, organizational design and leadership. I help you find the bottlenecks and propose concrete improvements that actually raise team efficiency.",
      sections: [
        {
          title: "When an audit makes sense",
          body: "Slow or unpredictable delivery, frequent production incidents, high senior attrition, chaotic priorities, weak communication between teams or with the business. Also before decisions about reorganization, splitting teams or larger hiring rounds.",
        },
        {
          title: "What the audit covers",
          body: "Structured interviews with managers, team leads and engineers. Analysis of core processes (planning, refinement, retros, code review, release management, incident management). A look at flow metrics, capacity and team structure.",
        },
        {
          title: "Output",
          body: "A concrete report with prioritized recommendations (P0/P1/P2), impact estimate and a proposed first 30/60/90 days. Where it makes sense, we follow up with hands-on implementation support.",
        },
        {
          title: "Engagement length",
          body: "Typically 2–4 weeks for the audit itself, then 0–6 months of hands-on support depending on scope. No long-contract commitment — we work in phases you can step out of at any time.",
        },
      ],
      bullets: [
        "Audit of core processes and structure",
        "Flow metrics and capacity analysis",
        "Prioritized recommendations with impact estimate",
        "Hands-on implementation support",
        "No extra dogmatic framework",
      ],
    },
    "service.softSkills": {
      eyebrow: "— Soft Skills —",
      lead:
        "Practical soft skills training for IT managers and key team members. Built on real cases from your environment, not on generic theoretical models.",
      sections: [
        {
          title: "Core areas",
          body: "Motivation, Leadership, Giving & Receiving Feedback, Coaching, Persuasion, Assertiveness, Conflict Resolution, Facilitation & Moderation, Goal Setting, Time Management, Burnout. Each can be packaged as a standalone module or rolled into a broader program.",
        },
        {
          title: "Training format",
          body: "0.5–1 day interactive format with role-plays, scripts for real conversations and concrete frameworks people can reuse the next day. On-site or remote (max 12 people for full interaction).",
        },
        {
          title: "Tailored preparation",
          body: "Before the session a short intro call with the buyer (manager/HR) — we map real situations the team is facing. Exercises and role-plays are then built on those, not on abstract examples.",
        },
        {
          title: "Who it is for",
          body: "Engineering managers, team leads, scrum masters, product owners, project managers and key seniors doing formal or informal leadership work.",
        },
      ],
      bullets: [
        "11 modular focus areas",
        "Real situations from your environment",
        "Role-play scenarios and templates",
        "0.5–1 day intensive format",
        "Take-home materials for immediate use",
      ],
    },
    "service.agile": {
      eyebrow: "— Agile / Scrum —",
      lead:
        "Practical Agile and Scrum training for IT teams that want outcomes, not more ceremonies. We work with your real backlog and metrics, not theoretical examples.",
      sections: [
        {
          title: "Who it is for",
          body: "Teams starting with Agile/Scrum. Teams that 'do Scrum but get no results'. Scrum masters and product owners looking for a step beyond the basic certificate. Engineering managers who want to understand what to actually expect from a team.",
        },
        {
          title: "What we cover",
          body: "Agile thinking and principles (the why, not just the how). Practical flow metrics — lead time, throughput, WIP, cycle time. Kanban and Scrum in real practice. Refinement, planning, retrospectives that make sense. Working with tech debt and capacity.",
        },
        {
          title: "Hands-on exercises",
          body: "Backlog refinement on your real backlog. Analysis of metrics from your tool (Jira, Linear, GitHub Projects). Designing a retrospective format the team will actually use. Stand-ups without empty status reading.",
        },
        {
          title: "Length and format",
          body: "1–2 day workshop (on-site recommended for full interaction), optional follow-up 30-day coaching support where we tackle real situations with the team in fast mode.",
        },
      ],
      bullets: [
        "Agile thinking, not just ceremonies",
        "Practical flow metrics",
        "Work on your real backlog and data",
        "Retrospectives the team will actually use",
        "Optional 30-day follow-up coaching",
      ],
    },
    "service.okr": {
      eyebrow: "— OKR —",
      lead:
        "I am an accredited OKR Mentor and help IT companies adopt OKRs in a practical, sustainable way that connects strategy with execution. No blind copying of the Google model.",
      sections: [
        {
          title: "OKR certification training",
          body: "1–2 day certification training for leadership, managers and OKR champions. We cover the difference between OKRs and KPIs, how to write quality Objectives and measurable Key Results, how to set up the cycle, check-ins and retros. Participants receive a certificate.",
        },
        {
          title: "Implementation support",
          body: "I help with the first cycle end to end: leadership alignment, designing the OKR tree across the organization, facilitating the first OKR setting, setting up the check-in rhythm (typically weekly/bi-weekly) and the retro at the end of the cycle.",
        },
        {
          title: "Restart or improvement of existing practice",
          body: "If you already have OKRs but do not feel results — I do an audit of the current state (Objectives, KRs, rhythm, roles) and propose concrete improvements. Often 2–3 changes are enough for the practice to start delivering value.",
        },
        {
          title: "Who it is for",
          body: "IT companies, scale-ups and departments of large corporations that want to improve prioritization and alignment across teams. Typically 20+ people — below that a lighter goal-setting format usually fits better.",
        },
      ],
      bullets: [
        "Accredited OKR Mentor",
        "1–2 day certification training",
        "Tailored first-cycle facilitation",
        "Audit and restart of existing practice",
        "No blind copying of external models",
      ],
    },
    "service.change": {
      eyebrow: "— Change Management —",
      lead:
        "Practical change management for IT companies going through reorganization, technology change, acquisition or rapid growth. No dogma, with concrete examples from practice.",
      sections: [
        {
          title: "When it makes sense",
          body: "Reorganization or splitting teams. Technology change (e.g. monolith → microservices, on-prem → cloud). Acquisition or integration of a new team. Introducing a new delivery model (waterfall → agile, ad-hoc → structured). Rapid growth past 30+ people where the previous practices stop working.",
        },
        {
          title: "Workshop for leadership and key people",
          body: "1 day workshop, 8–20 people. We cover change management models (Kotter, ADKAR, Bridges Transition Model) adapted to IT reality. Stakeholder mapping, communication plan, plan for the first 30/60/90 days. Hands-on exercises on your specific change.",
        },
        {
          title: "Follow-up support",
          body: "Optional 30–90 day support after the workshop — regular check-ins with leadership, sparring in critical moments, support with communication towards teams. No billing for small questions.",
        },
        {
          title: "What you actually get",
          body: "A concrete action plan the team can execute. Shared language between leadership and managers about how the change will be run. Reduced risk of resignations and internal opposition. Faster stabilization after the change.",
        },
      ],
      bullets: [
        "Kotter / ADKAR models in IT reality",
        "1 day workshop for 8–20 people",
        "Stakeholder mapping and communication plan",
        "30/60/90 day plan",
        "Optional 30–90 day follow-up",
      ],
    },
  },
};

// Shared UI copy for the new services index + detail pages.
export const PAGE_COPY = {
  cs: {
    servicesIndex: {
      lead:
        "Každá spolupráce se přizpůsobí vašemu kontextu, prostředí a obchodním cílům. Žádné zázračné recepty — jen praktická řešení, která fungují ve vaší realitě.",
      readMore: "Více o službě",
    },
    serviceDetail: {
      bulletsTitle: "Co reálně získáte",
      relatedTitle: "Další služby IT Impact",
      ctaTitle: "Chcete probrat váš konkrétní případ?",
      ctaText:
        "Vyberte si 30minutový úvodní hovor zdarma — projdeme váš kontext a navrhnu konkrétní další krok.",
      ctaButton: "Naplánovat úvodní hovor",
      backToServices: "Zpět na přehled služeb",
    },
  },
  en: {
    servicesIndex: {
      lead:
        "Every engagement is shaped around your context, environment and business goals. No silver bullets — only practical solutions that work in your reality.",
      readMore: "More about this service",
    },
    serviceDetail: {
      bulletsTitle: "What you actually get",
      relatedTitle: "Other IT Impact services",
      ctaTitle: "Want to discuss your specific case?",
      ctaText:
        "Pick a free 30-minute intro call — we go through your context and I propose a concrete next step.",
      ctaButton: "Schedule an intro call",
      backToServices: "Back to all services",
    },
  },
};

// Helper to retrieve the path for a given route id + language.
export const pathFor = (id, lang = "cs") => ROUTE_MAP[id]?.[lang] ?? "/";

// Build the full canonical URL for a given route id + language.
export const canonicalFor = (id, lang = "cs") =>
  `${SITE_URL}${pathFor(id, lang)}`;

// Build alternate-language URL pairs for hreflang tags.
export const alternatesFor = (id) => ({
  cs: `${SITE_URL}${ROUTE_MAP[id]?.cs ?? "/"}`,
  en: `${SITE_URL}${ROUTE_MAP[id]?.en ?? "/en/"}`,
});

export const OG_IMAGE_URL = OG_IMAGE;
